import React, { useContext, useEffect, useRef, useState } from "react";

import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import { Link, NavLink, Outlet } from "react-router-dom-v5-compat";

import { SearchField, LinkButton, Price, RecordFeatureView } from "./Atoms";
import { useBrand } from "./BrandContext";
import { OrderedOrderItems } from "./Cart/OrderedOrderItem";
import { PendingCartItem } from "./Cart/PendingCartItem";
import { getCartTotal } from "./Cart/selectors";
import OnlineOrderingDisabled from "./OnlineOrderingSchedule/OnlineOrderingDisabled";
import { SearchResults } from "./Sections/Section";
import { useSession } from "./Session/SessionContext";
import { getIsWishlist } from "./Session/sessionSelectors";
import { useVenue } from "./VenueContext";
import { getActiveSections, getPromotions } from "./menuSelectors";
import { useFetchOnlineOrderingSchedule } from "./queries";
import type { Venue, Menu, Section } from "./schema";
import { getVenueHasMobileOrdering } from "./venueSelectors";

interface MainPageProps {
  menu: Menu;
  smallView?: boolean;
  isRootPage?: boolean;
}
const hasLogoImage = ({ logoImageUrl }: Venue) =>
  logoImageUrl && logoImageUrl != "/assets/missing.png";
export const LogoImage = ({ venue }: { venue: Venue }) => {
  const { isPlayful, isElegant, isSimple, isBrutalist, ...venueBrand } =
    useBrand();

  if (!hasLogoImage(venue)) {
    return null;
  }
  return (
    <div className="aspect-w-1 aspect-h-1 ">
      <img
        className={classNames(
          "bg-venue-primary rounded-venue-brand object-cover ",
        )}
        src={venue.logoImageUrl}
      />
    </div>
  );
};

interface SectionBoxProps {
  section: Section;
  forceMobileLayout?: boolean;
  isActive?: boolean;
}

export const SectionBox = ({
  section,
  forceMobileLayout,
  isActive,
}: SectionBoxProps) => {
  const sectionImage = section.items.find(
    (i) => i.heroImageThumbnailUrl,
  )?.heroImageThumbnailUrl;

  const hasLongWord = section.name.split(" ").some((word) => word.length > 10);

  return (
    <div
      className={classNames("rounded-venue-brand space-y-8 ", {
        "md:bg-venue-highlight md:text-venue-secondary md:group is-active":
          isActive,
        "md:p-8": !forceMobileLayout,
      })}
    >
      <div className="w-[72px] h-[72px]">
        {sectionImage ? (
          <img
            className="object-cover h-full w-full rounded-venue-brand"
            src={sectionImage}
          />
        ) : (
          <div className="flex  text-sm font-medium text-venue-secondary rounded-venue-brand items-center justify-center bg-venue-primary text-center h-full  group-[.is-active]:bg-venue-secondary group-[.is-active]:text-venue-primary">
            {section.name}
          </div>
        )}
      </div>
      {sectionImage && (
        <div
          className={classNames(
            "flex-1 font-medium text-center text-ellipsis overflow-clip w-[72px]",
            {
              "text-xs": hasLongWord,
              "text-sm": !hasLongWord,
            },
          )}
        >
          {section.name}
        </div>
      )}
    </div>
  );
};

export const SectionsGrid = ({
  menu,
  forceMobileLayout,
}: {
  menu: Menu;
  forceMobileLayout?: boolean;
  gridLayout?: boolean;
}) => {
  const sections = getActiveSections(menu);
  const { t } = useTranslation();
  const promotions = getPromotions(menu);
  const promotionSection: Section = {
    uuid: "promotions",
    name: t("promotions.title"),
    pricingType: "simple",
    items: promotions,
  };
  return (
    <div
      className={classNames(
        "flex min-w-0 max-w-full overflow-x-auto space-x-16 ",
        { "md:-mx-8 md:space-x-12": !forceMobileLayout },
      )}
    >
      {promotions.length > 0 && (
        <NavLink
          to={"/"}
          children={({ isActive }) => (
            <SectionBox
              isActive={isActive}
              forceMobileLayout={forceMobileLayout}
              key={promotionSection.uuid}
              section={promotionSection}
            />
          )}
        />
      )}
      {sections.map((section) => (
        <NavLink
          key={section.uuid}
          to={section.uuid}
          children={({ isActive }) => (
            <SectionBox
              isActive={isActive}
              forceMobileLayout={forceMobileLayout}
              key={section.uuid}
              section={section}
            />
          )}
        />
      ))}
    </div>
  );
};

export const VenueWelcome = () => {
  const venue = useVenue();
  const { t } = useTranslation();
  return (
    <div className="md:flex-1 md:flex md:items-center md:space-x-16 space-y-12 md:space-y-0">
      {hasLogoImage(venue) && (
        <div className="w-96 md:text-right mx-auto md:mx-0">
          <Link
            to={`/`}
            className="block max-w-[96px] mx-auto text-center w-full space-y-8"
          >
            <LogoImage venue={venue} />
          </Link>
        </div>
      )}
      <div className="md:flex-1 space-y-16">
        <div className="text-center text-venue-primary text-xl font-extrabold">
          <Trans
            i18nKey="mainPage.welcomeToVenue"
            values={{ venueName: venue.name }}
            shouldUnescape
          />
        </div>
        <div className="mx-auto text-sm font-normal whitespace-pre-wrap">
          {venue.venueBrand.description}
        </div>
      </div>
    </div>
  );
};

const MenuFilter = ({
  searchTerm,
  onSearchChange,
}: {
  searchTerm?: string;
  onSearchChange: (newTerm: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-8 ">
      <SearchField searchTerm={searchTerm} onChange={onSearchChange} />
    </div>
  );
};

const MainPage = ({ menu, isRootPage }: MainPageProps) => {
  const { t } = useTranslation();

  const isLayoutPage = !isRootPage;
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const onSearchChange = (newTerm: string) => {
    setSearchTerm(newTerm);
  };

  return (
    <RecordFeatureView featureName="Online / Menu">
      <div
        className={classNames("md:py-32 md:flex space-x-48 pb-96 relative", {
          "py-16": isRootPage,
        })}
      >
        <div className={classNames("md:flex-1", { "space-y-24": isRootPage })}>
          <div
            className={classNames("space-y-24 md:flex flex-1 md:space-x-32", {
              "hidden md:flex": isLayoutPage,
            })}
          >
            <div className="md:min-w-0 md:w-0 md:flex-1 space-y-24 md:space-y-32">
              {!searchTerm && (
                <>
                  <VenueWelcome />
                  <SectionsGrid menu={menu} />
                  <OnlineOrderingDisabled />
                </>
              )}
              {isRootPage && (
                <div className="md:hidden">
                  <MenuFilter
                    searchTerm={searchTerm}
                    onSearchChange={onSearchChange}
                  />
                </div>
              )}
            </div>
          </div>
          {searchTerm ? (
            <SearchResults
              resetSearch={() => setSearchTerm("")}
              menu={menu}
              searchTerm={searchTerm}
            />
          ) : (
            <Outlet />
          )}
        </div>
        <div
          className={classNames(
            "md:max-w-sm md:flex-1 md:flex-col md:h-screen max-h-screen min-h-0 md:sticky self-start py-32 md:top-0 hidden md:block space-y-24",
            {},
          )}
        >
          <div className="flex-none ">
            <MenuFilter
              searchTerm={searchTerm}
              onSearchChange={onSearchChange}
            />
          </div>
          <div className="flex-1">
            <SideBarCart />
          </div>
        </div>
      </div>
    </RecordFeatureView>
  );
};

export default MainPage;

const ScrollIntoView = ({
  parentRef,
  children,
}: {
  parentRef: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref?.current && parentRef?.current) {
      const parentNode = parentRef?.current;
      parentNode.scrollTop = ref.current.offsetTop;
    }
  }, []);
  return (
    <div
      className="scroll-smooth"
      style={{ scrollMarginTop: "-400px" }}
      ref={ref}
    >
      {children}
    </div>
  );
};
const SideBarCart = () => {
  const { t } = useTranslation();
  const isWishList = getIsWishlist(useSession(), useVenue());
  const orderingEnabled = getVenueHasMobileOrdering(useVenue());
  const { cart } = useSession();
  const totalOrderPrice = getCartTotal(cart);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  return (
    <div className="">
      <div
        ref={scrollContainerRef}
        className="scroll-smooth h-[85vh] overflow-y-auto min-h-0 border-venue-tertiary border-[6px] rounded-venue-brand px-12 space-y-24"
      >
        <h3 className="sticky z-10 py-12 top-0 bg-venue-secondary  text-2xl font-bold text-center ">
          {orderingEnabled ? t("cart.yourOrder") : t("cart.yourWishlist")}
        </h3>
        <div className="space-y-12 ">
          <div className="overflow-y-auto space-y-12 -mx-2 p-2">
            {cart.items.length > 0 ? (
              cart.items.map((cartItem) => (
                <ScrollIntoView
                  parentRef={scrollContainerRef}
                  key={cartItem.id}
                >
                  <PendingCartItem key={cartItem.id} cartItem={cartItem} />
                </ScrollIntoView>
              ))
            ) : (
              <div className="text-center text-lg font-medium py-24">
                {t("cart.noPendingOrders")}
              </div>
            )}
          </div>
        </div>
        <OrderedOrderItems />
        <div className="sticky space-y-16 py-12 bottom-0 bg-venue-secondary">
          <div className="flex justify-end">
            <h3 className="text-2xl font-bold flex-1">{t("cart.total")} </h3>
            <div className="text-2xl font-extrabold">
              <Price showCurrency value={totalOrderPrice} />{" "}
            </div>
          </div>
          <LinkButton primary to="/your-order/pending">
            {isWishList ? t("mainPage.yourWishlist") : t("mainPage.yourOrder")}
          </LinkButton>
        </div>
      </div>
    </div>
  );
};
